import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback } from 'react';
import { useFireAdControlAnalyticsEvents, useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { createSurfaceLoader, createSurfaceRenderer, createSurfaceSdk } from '@atlassian/surface-sdk';
import { ExperienceSuccess, useUFOComponentExperience } from '@atlassian/ufo';
import { useAnalyticsSideEffect } from './controllers/analytics-side-effects';
import { createContextMiddleware } from './controllers/context-middlewares';
import { observabilitySideEffects } from './controllers/observability-side-effects';
import { AppRecommendations } from './ui/app-recommendations';
import { AppRecommendationsSectionHeader } from './ui/app-recommendations/app-recommendation-section-header';
import { AppRecommendationsList } from './ui/app-recommendations/app-recommendations-list';
import { AppRecommendationsWithTitle } from './ui/app-recommendations/app-recommendations-with-title';
import { showRecommendations } from './utils/ufo';
const gqlEndpoint = '/gateway/api/graphql';
const SurfaceRenderer = createSurfaceRenderer({
  AppRecommendationsSectionHeader,
  AppRecommendationsList,
  AppRecommendations,
  AppRecommendationsWithTitle
});
const surfaces = {
  appSwitcher: {
    surfaceType: 'DiscoverySection',
    surfaceKey: 'app-recommendations-surface',
    contextDependencies: ['tenantId', 'subproduct', 'sessionId', 'locale', 'isSiteAdmin', 'workspaceId', 'orgId', {
      field: 'product',
      required: true
    }, {
      field: 'switcherMenuHasBeenOpened',
      required: true
    }],
    fallback: null
  }
};
const disabledSdk = {
  DiscoverySurface: () => {
    return null;
  },
  setContext: c => c,
  useDiscoverySdk: () => ({
    setContext: c => c
  })
};
export const createDiscoverySdk = config => {
  var _config$surfaceLoader, _config$graphqlEndpoi, _config$sideEffects;
  if ((config === null || config === void 0 ? void 0 : config.enable) === false) {
    return disabledSdk;
  }
  const surfaceLoader = (_config$surfaceLoader = config === null || config === void 0 ? void 0 : config.surfaceLoader) !== null && _config$surfaceLoader !== void 0 ? _config$surfaceLoader : createSurfaceLoader({
    gqlEndpoint: (_config$graphqlEndpoi = config === null || config === void 0 ? void 0 : config.graphqlEndpoint) !== null && _config$graphqlEndpoi !== void 0 ? _config$graphqlEndpoi : gqlEndpoint
  });
  const {
    useSurface,
    updateContext
  } = createSurfaceSdk({
    surfaces,
    surfaceLoader,
    context: {
      ...(config === null || config === void 0 ? void 0 : config.context)
    },
    sideEffects: [observabilitySideEffects, ...((_config$sideEffects = config === null || config === void 0 ? void 0 : config.sideEffects) !== null && _config$sideEffects !== void 0 ? _config$sideEffects : [])],
    contextMiddleware: createContextMiddleware()
  });
  return {
    DiscoverySurface: ({
      surface,
      fallback,
      baseHeadingLevel,
      errorFallback
    }) => {
      const analyticsSideEffect = useAnalyticsSideEffect(useFireAdControlAnalyticsEvents(), useFireCrossFlowAnalyticsEvents());
      const surfaceProps = useSurface(surface, analyticsSideEffect);
      useUFOComponentExperience(showRecommendations);
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SurfaceRenderer, _extends({}, surfaceProps, {
        fallback: fallback !== null && fallback !== void 0 ? fallback : surfaceProps.fallback,
        errorFallback: errorFallback,
        baseHeadingLevel: baseHeadingLevel
      })), /*#__PURE__*/React.createElement(ExperienceSuccess, {
        experience: showRecommendations
      }));
    },
    setContext: updateContext,
    useDiscoverySdk: () => {
      const setContextCallback = useCallback(updateContext, []);
      return {
        setContext: setContextCallback
      };
    }
  };
};